<template>
  <v-card>
    <v-overlay :value="loaderOverlay" :absolute="true">
      <v-row justify="center">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-row>
    </v-overlay>
    <v-card-title>
      <v-row justify="start" no-gutters>
        <v-icon large class="pr-2">mdi-calendar-clock</v-icon>
        <h3>{{ $t("timeModels") }}</h3>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-row justify="center" class="mt-10 mb-4" no-gutters>
        <v-col cols="12">
          <v-data-table
            v-model="dailyModelListSelected"
            :headers="dailyModelListHeaders"
            :items="filteredDailyModelList"
            :items-per-page="3"
            item-key="id"
            hide-default-footer
            class="elevation-1 table-header"
            @page-count="dailyPageCount = $event"
            :page.sync="dailyPage"
          >
            <template v-slot:header.dailyModelName="{ header }">
              <div class="d-flex flex-column">
                <span class="caption font-weight-bold">{{ header.text }}</span>
                <v-text-field
                  v-model="dailyNameFilter"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  hide-details
                  dense
                  solo
                  @click.stop
                  @mousedown.stop
                ></v-text-field>
              </div>
            </template>
            <template v-slot:header.dailyModelText="{ header }">
              <div class="d-flex flex-column">
                <span class="caption font-weight-bold">{{ header.text }}</span>
                <v-text-field
                  v-model="dailyRemarkFilter"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  hide-details
                  dense
                  solo
                  @click.stop
                  @mousedown.stop
                ></v-text-field>
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom color="primary">
                <template slot="activator" slot-scope="{ on }">
                  <v-icon @click="getDailyModel(item.id)" class="mr-2 primary--text" :style="{ cursor: 'pointer' }" v-on="on"> mdi-pencil </v-icon>
                </template>
                <span class="px-1">{{ $t("edit") }}</span>
              </v-tooltip>
              <v-tooltip bottom color="primary">
                <template slot="activator" slot-scope="{ on }">
                  <v-icon @click="deleteDailyModel(item)" class="primary--text" :style="{ cursor: 'pointer' }" v-on="on"> mdi-delete </v-icon>
                </template>
                <span class="px-1">{{ $t("delete") }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
          <div class="text-center pt-2">
            <v-pagination v-model="dailyPage" :length="dailyPageCount"></v-pagination>
          </div>
        </v-col>
      </v-row>
      <v-row justify="end" class="mb-2" no-gutters>
        <v-btn rounded color="primary" @click="ShowDailyDialog = true">{{ $t("addDailyModel") }}</v-btn>
      </v-row>

      <v-row justify="end" class="mt-10 mb-4" no-gutters>
        <v-switch
          :label="!showInactiveWeekModels ? $t('showInactiveWeekModels') : $t('hideInactiveWeekModels')"
          class="mt-2"
          v-model="showInactiveWeekModels"
          hide-details
        ></v-switch>
        <v-col cols="12">
          <v-data-table
            v-model="weekModelListSelected"
            :headers="weekModelListHeaders"
            :items="filteredWeekModelsForTable"
            item-key="id"
            class="elevation-1 table-header"
            :items-per-page="3"
            hide-default-footer
            @page-count="weekPageCount = $event"
            :page.sync="weekPage"
            :item-class="setBackgroundColor"
          >
            <template v-slot:header.weekModelName="{ header }">
              <div class="d-flex flex-column">
                <span class="caption font-weight-bold">{{ header.text }}</span>
                <v-text-field
                  v-model="weekNameFilter"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  hide-details
                  dense
                  solo
                  @click.stop
                  @mousedown.stop
                ></v-text-field>
              </div>
            </template>
            <template v-slot:header.weekModelText="{ header }">
              <div class="d-flex flex-column">
                <span class="caption font-weight-bold">{{ header.text }}</span>
                <v-text-field
                  v-model="weekRemarkFilter"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  hide-details
                  dense
                  solo
                  @click.stop
                  @mousedown.stop
                ></v-text-field>
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom color="primary">
                <template slot="activator" slot-scope="{ on }">
                  <v-icon @click="getWeekModel(item.id)" class="mr-2 primary--text" :style="{ cursor: 'pointer' }" v-on="on"> mdi-pencil </v-icon>
                </template>
                <span class="px-1">{{ $t("edit") }}</span>
              </v-tooltip>
              <v-tooltip v-if="item.inUse" bottom color="primary">
                <template slot="activator" slot-scope="{ on }">
                  <v-icon @click="deleteWeekModel(item)" class="primary--text" :style="{ cursor: 'pointer' }" v-on="on"> mdi-delete </v-icon>
                </template>
                <span class="px-1">{{ $t("delete") }}</span>
              </v-tooltip>
              <v-tooltip v-else-if="!item.inUse && !item.active" bottom color="primary">
                <template slot="activator" slot-scope="{ on }">
                  <v-icon @click="activateWeekModel(item)" class="primary--text" :style="{ cursor: 'pointer' }" v-on="on"> mdi-file-document-check </v-icon>
                </template>
                <span class="px-1">{{ $t("activate") }}</span>
              </v-tooltip>
              <v-tooltip v-else bottom color="primary">
                <template slot="activator" slot-scope="{ on }">
                  <v-icon @click="deactivateWeekModel(item)" class="primary--text" :style="{ cursor: 'pointer' }" v-on="on"> mdi-file-document-remove </v-icon>
                </template>
                <span class="px-1">{{ $t("deactivate") }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
          <div class="text-center pt-2">
            <v-pagination v-model="weekPage" :length="weekPageCount"></v-pagination>
          </div>
        </v-col>
      </v-row>

      <v-row justify="end" class="mb-2" no-gutters>
        <v-btn rounded color="primary" @click="ShowWeeklyDialog = true">{{ $t("addWeeklyModel") }}</v-btn>
      </v-row>
      <v-row justify="center" class="mt-10 mb-4" no-gutters>
        <v-col cols="12">
          <v-data-table
            :headers="autoWeekModelsHeaders"
            :items="filteredAutoWeekModels"
            item-key="id"
            class="elevation-1 table-header"
            :items-per-page="3"
            hide-default-footer
            @page-count="autoModelPageCount = $event"
            :page.sync="autoModelPage"
          >
            <template v-slot:header.name="{ header }">
              <div class="d-flex flex-column">
                <span class="caption font-weight-bold">{{ header.text }}</span>
                <v-text-field
                  v-model="autoNameFilter"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  hide-details
                  dense
                  solo
                  @click.stop
                  @mousedown.stop
                ></v-text-field>
              </div>
            </template>
            <template v-slot:header.commit="{ header }">
              <div class="d-flex flex-column">
                <span class="caption font-weight-bold">{{ header.text }}</span>
                <v-text-field
                  v-model="autoRemarkFilter"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  hide-details
                  dense
                  solo
                  @click.stop
                  @mousedown.stop
                ></v-text-field>
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom color="primary">
                <template slot="activator" slot-scope="{ on }">
                  <v-icon @click="getAutoWeekModel(item.id)" class="mr-2 primary--text" :style="{ cursor: 'pointer' }" v-on="on"> mdi-pencil </v-icon>
                </template>
                <span class="px-1">{{ $t("edit") }}</span>
              </v-tooltip>
              <v-tooltip bottom color="primary">
                <template slot="activator" slot-scope="{ on }">
                  <v-icon v-if="!item.locked" @click="deleteAutoWeekModel(item)" class="primary--text" :style="{ cursor: 'pointer' }" v-on="on">
                    mdi-delete
                  </v-icon>
                </template>
                <span class="px-1">{{ $t("delete") }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
          <div class="text-center pt-2">
            <v-pagination v-model="autoModelPage" :length="autoModelPageCount"></v-pagination>
          </div>
        </v-col>
      </v-row>

      <v-row justify="end" class="mb-2" no-gutters>
        <v-btn rounded color="primary" @click="showAutoWeekModel = true">{{ $t("addAutoWeekModel") }}</v-btn>
      </v-row>
    </v-card-text>
    <v-card-actions></v-card-actions>

    <DailyDialog
      v-if="ShowDailyDialog"
      :dailyModelId="dailyModelId"
      @close="
        dailyModelId = 0;
        ShowDailyDialog = false;
        refreshModels();
      "
    />
    <WeeklyDialog
      v-if="ShowWeeklyDialog"
      :weekDailyModelList="weekDailyModelList"
      :weekModelId="weekModelId"
      :dailyModelList="dailyModelList"
      @close="
        weekModelId = 0;
        ShowWeeklyDialog = false;

        refreshModels();
      "
    />
    <AutoWeekModel
      v-if="showAutoWeekModel"
      :WeekModel="weekAutoModel"
      :saveMode="weekAutoModel.id != null ? true : false"
      @close="
        showAutoWeekModel = false;
        refreshModels();
        setBackAutoWeekModel();
      "
    />
  </v-card>
</template>

<script>
import DailyDialog from "./WorktimeAccount/DailyDialog";
import WeeklyDialog from "./WorktimeAccount/WeeklyDialog";
import AutoWeekModel from "./WorktimeAccount/AutoWeekModel";

export default {
  name: "WorkTimeAccount",
  components: { DailyDialog, WeeklyDialog, AutoWeekModel },

  data: (vm) => ({
    workTimeAccountModel: true,
    ShowDailyDialog: false,
    ShowWeeklyDialog: false,
    dailyPage: 1,
    dailyPageCount: 0,

    autoModelPage: 1,
    autoModelPageCount: 0,

    weekPage: 1,
    weekPageCount: 0,

    dailyLoader: false,
    weeklyLoader: false,

    saveMode: false,
    showInactiveWeekModels: false,

    weekModelListDropdown: [],
    weekModelList: [],
    filteredWeekModelList: [],
    weekModelListSelected: [],
    weekNameFilter: "",
    weekRemarkFilter: "",
    weekModelListHeaders: [
      {
        text: "id",
        value: "id",
        hint: "id",
        align: "start",
        sortable: true,
        class: "font-weight-bold subheading"
      },
      {
        text: vm.$t("name"),
        value: "weekModelName",
        hint: vm.$t("name"),
        align: "start",
        sortable: true,
        class: "font-weight-bold subheading"
      },
      {
        text: vm.$t("remark"),
        value: "weekModelText",
        hint: vm.$t("remark"),
        align: "start",
        sortable: true,
        class: "font-weight-bold subheading"
      },
      {
        text: "Actions",
        value: "actions",
        hint: "Actions",
        align: "center",
        sortable: false,
        class: "font-weight-bold subheading"
      }
    ],
    weekModal: false,

    userTimeModell: "",
    userTimeModelDate: "",
    userTimeModelDateDisabled: true,
    hardModel: false,
    dailyModelList: [],
    isDailyModelValid: false,
    endFromMinTime: "",
    dailyModelListSelected: [],
    dailyNameFilter: "",
    dailyRemarkFilter: "",
    dailyModelListHeaders: [
      {
        text: "id",
        value: "id",
        hint: "id",
        align: "start",
        sortable: true,
        class: "font-weight-bold subheading"
      },
      {
        text: vm.$t("name"),
        value: "dailyModelName",
        hint: vm.$t("name"),
        align: "start",
        sortable: true,
        class: "font-weight-bold subheading"
      },
      {
        text: vm.$t("remark"),
        value: "dailyModelText",
        hint: vm.$t("remark"),
        align: "start",
        sortable: true,
        class: "font-weight-bold subheading"
      },
      {
        text: "Actions",
        value: "actions",
        hint: "Actions",
        align: "center",
        sortable: false,
        class: "font-weight-bold subheading"
      }
    ],

    timeAccounts: [],
    weekDailyModelList: [],
    dailyModelId: 0,
    weekModelId: 0,
    showAutoWeekModel: false,
    loaderOverlay: true,
    autoWeekModels: [],
    autoNameFilter: "",
    autoRemarkFilter: "",
    autoWeekModelsHeaders: [
      {
        text: "id",
        value: "id",
        hint: "id",
        align: "start",
        sortable: true,
        class: " font-weight-bold subheading"
      },
      {
        text: vm.$t("name"),
        value: "name",
        hint: vm.$t("name"),
        align: "start",
        sortable: true,
        class: "font-weight-bold subheading"
      },
      {
        text: vm.$t("remark"),
        value: "commit",
        hint: vm.$t("remark"),
        align: "start",
        sortable: true,
        class: "font-weight-bold subheading"
      },
      {
        text: "Actions",
        value: "actions",
        hint: "Actions",
        align: "center",
        sortable: false,
        class: "font-weight-bold subheading"
      }
    ],
    weekAutoModel: {
      id: null,
      name: "",
      monday: {
        dayId: null,
        periods: [],
        workTime: null,
        pause: null,
        type: 0
      },
      tuesday: {
        dayId: null,
        periods: [],
        workTime: null,
        pause: null,
        type: 0
      },
      wednesday: {
        dayId: null,
        periods: [],
        workTime: null,
        pause: null,
        type: 0
      },
      thursday: {
        dayId: null,
        periods: [],
        workTime: null,
        pause: null,
        type: 0
      },
      friday: {
        dayId: null,
        periods: [],
        workTime: null,
        pause: null,
        type: 0
      },
      saturday: {
        dayId: null,
        periods: [],
        workTime: null,
        pause: null,
        type: 0
      },
      sunday: {
        dayId: null,
        periods: [],
        workTime: null,
        pause: null,
        type: 0
      },
      commit: "",
      validFrom: null,
      validUntil: null,
      exclusionPeriods: []
    }
  }),
  beforeDestroy: function () {
    this.$emit("close");
  },
  watch: {
    workTimeAccountModel: function (val) {
      if (!val) {
        this.$destroy();
      }
    },
    showInactiveWeekModels: function (val) {
      if (!val)
        this.filteredWeekModelList = this.weekModelList.filter(function (item) {
          return item.active;
        });
      else this.filteredWeekModelList = this.weekModelList;
    }
  },
  computed: {
    filteredDailyModelList: function () {
      let nameFilter = this.dailyNameFilter ? this.dailyNameFilter.toLowerCase() : "";
      let remarkFilter = this.dailyRemarkFilter ? this.dailyRemarkFilter.toLowerCase() : "";
      return this.dailyModelList.filter(function (item) {
        let matchName = !nameFilter || (item.dailyModelName && item.dailyModelName.toLowerCase().includes(nameFilter));
        let matchRemark = !remarkFilter || (item.dailyModelText && item.dailyModelText.toLowerCase().includes(remarkFilter));
        return matchName && matchRemark;
      });
    },
    filteredWeekModelsForTable: function () {
      let nameFilter = this.weekNameFilter ? this.weekNameFilter.toLowerCase() : "";
      let remarkFilter = this.weekRemarkFilter ? this.weekRemarkFilter.toLowerCase() : "";
      return this.filteredWeekModelList.filter(function (item) {
        let matchName = !nameFilter || (item.weekModelName && item.weekModelName.toLowerCase().includes(nameFilter));
        let matchRemark = !remarkFilter || (item.weekModelText && item.weekModelText.toLowerCase().includes(remarkFilter));
        return matchName && matchRemark;
      });
    },
    filteredAutoWeekModels: function () {
      let nameFilter = this.autoNameFilter ? this.autoNameFilter.toLowerCase() : "";
      let remarkFilter = this.autoRemarkFilter ? this.autoRemarkFilter.toLowerCase() : "";
      return this.autoWeekModels.filter(function (item) {
        let matchName = !nameFilter || (item.name && item.name.toLowerCase().includes(nameFilter));
        let matchRemark = !remarkFilter || (item.commit && item.commit.toLowerCase().includes(remarkFilter));
        return matchName && matchRemark;
      });
    }
  },
  created: function () {
    this.refreshModels();
  },
  methods: {
    async refreshModels() {
      this.loaderOverlay = true;
      await this.getDayTimeModels();
      await this.getWeekModels();
      await this.getAutoWeekModels();
      this.loaderOverlay = false;
    },
    async getDayTimeModels() {
      await this.$store
        .dispatch("server/timeAccounts/getDayTimeModels")
        .then((resp) => {
          this.dailyModelList = resp.data.data;
          this.weekDailyModelList = [];
          for (var i = 0; i < resp.data.data.length; i++) {
            this.weekDailyModelList.push({
              text: resp.data.data[i].dailyModelName,
              value: resp.data.data[i].id
            });
          }
          this.dailyLoader = false;
        })
        .catch((err) => {
          this.dailyLoader = false;
          console.log(err);
          Helper.showAlert(app, err, "error");
        });
    },
    getDailyModel(id) {
      this.dailyModelId = id;
      this.ShowDailyDialog = true;
    },
    getWeekModel(id) {
      this.weekModelId = id;
      this.ShowWeeklyDialog = true;
    },
    async getWeekModels() {
      await this.$store
        .dispatch("server/timeAccounts/getWeekModels")
        .then((resp) => {
          if (resp.data.success) {
            this.weekModelList = resp.data.data;
            if (!this.showInactiveWeekModels)
              this.filteredWeekModelList = this.weekModelList.filter(function (item) {
                return item.active;
              });
            else this.filteredWeekModelList = this.weekModelList;
            this.weekModelListDropdown = [];
            for (let i = 0; i < resp.data.data.length; i++) {
              this.weekModelListDropdown.push({
                text: resp.data.data[i].weekModelName,
                value: resp.data.data[i].weekModelId
              });
            }
          } else {
            Helper.showAlert(app, data.error, "error");
          }

          this.weeklyLoader = false;
        })
        .catch((err) => {
          this.weeklyLoader = false;
          console.log(err);
          Helper.showAlert(app, err, "error");
        });
    },
    deleteDailyModel(dailyModelId) {
      // Trigger an Alert dialog
      let message = "Möchten Sie wirklich das Zeitmodell <br><b>'" + dailyModelId.dailyModelName + "'</b><br>löschen?";
      this.loaderOverlay = true;

      this.$dialog
        .confirm(message, {
          okText: this.$t("ok"),
          cancelText: this.$t("cancel")
        })
        .then(() => {
          this.$store
            .dispatch("server/timeAccounts/deleteDayTimeModel", dailyModelId.id)
            .then((resp) => {
              if (resp.data.success) {
                this.$eventHub.$emit("showSuccess", this.$t("timeModelDeleted"));
                this.refreshModels();
              } else if (resp.data.error !== "") {
                this.$eventHub.$emit("showError", this.$t(resp.data.error));
              } else {
                this.$eventHub.$emit("showError", this.$t("unexpectedError"));
              }
            })
            .catch((err) => {
              console.log(err);
              this.$eventHub.$emit("showError", this.$t("unexpectedError"));
            });
        })
        .catch((err) => {
          console.log(err);
          return false;
        });
      this.loaderOverlay = false;
    },
    deleteWeekModel(weekModel) {
      // Trigger an Alert dialog
      let message = "Möchten Sie wirklich das Wochenmodell <br><b>'" + weekModel.weekModelName + "'</b><br>löschen?";
      this.loaderOverlay = true;
      this.$dialog
        .confirm(message, {
          okText: this.$t("ok"),
          cancelText: this.$t("cancel")
        })
        .then(() => {
          this.$store
            .dispatch("server/timeAccounts/deleteWeekModel", weekModel.id)
            .then((resp) => {
              if (resp.data.success) {
                this.$eventHub.$emit("showSuccess", this.$t("weekModelDeleted"));
                this.refreshModels();
              } else if (resp.data.error !== "") {
                this.$eventHub.$emit("showError", this.$t(resp.data.error));
              } else {
                this.$eventHub.$emit("showError", this.$t("unexpectedError"));
              }
            })
            .catch((err) => {
              console.log(err);
              this.$eventHub.$emit("showError", this.$t("unexpectedError"));
            });
        })
        .catch((err) => {
          console.log(err);
          return false;
        });
      this.loaderOverlay = false;
    },
    async getAutoWeekModels() {
      await this.$store
        .dispatch("server/timeAccounts/getAutoWeekModels")
        .then((resp) => {
          if (resp.data.success) {
            this.autoWeekModels = resp.data.data;
          } else {
            Helper.showAlert(app, data.error, "error");
          }

          this.weeklyLoader = false;
        })
        .catch((err) => {
          this.weeklyLoader = false;
          console.log(err);
          Helper.showAlert(app, err, "error");
        });
    },
    getAutoWeekModel(id) {
      this.weekAutoModel = this.autoWeekModels.find(function (item) {
        return item.id == id;
      });
      this.showAutoWeekModel = true;
    },
    async deleteAutoWeekModel(weekModel) {
      let message = this.$t("deleteWeekModelMessage").format(weekModel.name);

      this.$dialog
        .confirm(message, {
          okText: this.$t("ok"),
          cancelText: this.$t("cancel")
        })
        .then(async () => {
          await this.$store
            .dispatch("server/timeAccounts/deleteAutoWeekModel", weekModel.id)
            .then((resp) => {
              this.weeklyLoader = false;
            })
            .catch((err) => {
              this.weeklyLoader = false;
              console.log(err);
              Helper.showAlert(app, err, "error");
            });
          this.refreshModels();
        })
        .catch((err) => {
          console.log(err);
          return false;
        });
    },
    deactivateWeekModel(weekModel) {
      weekModel.inUse = false;
      let message = this.$t("deactivateWeekModelMessage").format(weekModel.name);

      this.$dialog
        .confirm(message, {
          okText: this.$t("ok"),
          cancelText: this.$t("cancel")
        })
        .then(async () => {
          await this.$store
            .dispatch("server/timeAccounts/deactivateWeekModel", weekModel)
            .then((resp) => {
              this.weekModal = false;

              if (resp.data.success) this.$eventHub.$emit("showSuccess", this.$t("weekModelSuccessfullyDeactivated"));
              else this.$eventHub.$emit("showError", this.$t(resp.data.error));
            })
            .catch((err) => {
              console.log(err);
              this.$eventHub.$emit("showError", err);
            });
          this.refreshModels();
        })
        .catch((err) => {
          console.log(err);
          return false;
        });
    },
    async activateWeekModel(weekModel) {
      await this.$store
        .dispatch("server/timeAccounts/activateWeekModel", weekModel)
        .then((resp) => {
          this.weekModal = false;

          if (resp.data.success) this.$eventHub.$emit("showSuccess", this.$t("weekModelSuccessfullyDeactivated"));
          else this.$eventHub.$emit("showError", this.$t(resp.data.error));
        })
        .catch((err) => {
          console.log(err);
          this.$eventHub.$emit("showError", err);
        });
      this.refreshModels();
    },
    setBackgroundColor(item) {
      if (item.active) {
        return "row-active";
      } else {
        return "row-inactive";
      }
    },
    setBackAutoWeekModel() {
      this.weekAutoModel = {
        id: null,
        name: "",
        monday: {
          dayId: null,
          periods: [],
          workTime: null,
          pause: null,
          type: 0
        },
        tuesday: {
          dayId: null,
          periods: [],
          workTime: null,
          pause: null,
          type: 0
        },
        wednesday: {
          dayId: null,
          periods: [],
          workTime: null,
          pause: null,
          type: 0
        },
        thursday: {
          dayId: null,
          periods: [],
          workTime: null,
          pause: null,
          type: 0
        },
        friday: {
          dayId: null,
          periods: [],
          workTime: null,
          pause: null,
          type: 0
        },
        saturday: {
          dayId: null,
          periods: [],
          workTime: null,
          pause: null,
          type: 0
        },
        sunday: {
          dayId: null,
          periods: [],
          workTime: null,
          pause: null,
          type: 0
        },
        commit: "",
        validFrom: null,
        validUntil: null,
        exclusionPeriods: []
      };
    }
  }
};
</script>

<style scoped>
.row-inactive {
  background-color: #cfcfcf;
}
</style>
